import React, { Suspense, useContext, useEffect, useState } from 'react'
import {
  BrowserRouter,
  Route,
  Switch,
  RouteComponentProps,
  Redirect,
} from 'react-router-dom'

import { LoadingPage, Page404 } from '@dataplace.ai/ui-components/pages'
import { RouteChildType, RoutePropsType, RouteType } from '@dataplace.ai/types'
import { Link } from '@dataplace.ai/ui-components/atoms'
import { redirectToSignOut } from '@dataplace.ai/functions/utils'
import { AuthContext } from './AuthContext'
import { FlashMessagesProvider } from './FlashMessages'

const renderRoutes = (routes: RouteType[]) => routes.map(({
  component: Component,
  guard,
  children,
  rootPath,
  id,
  redirect,
}) => {
  const ParentGuard = guard || React.Fragment

  const parentRoute = redirect || Component
    ? (
      <Route
        key={`route-${id}`}
        exact
        path={rootPath}
        render={(props) => (
          <ParentGuard>
            {Component && <Component {...props} />}
            {redirect && <Redirect to={redirect} />}
          </ParentGuard>
        )}
      />
    )
    : null

  const childrenRoutes = children
    ? children.map((element: RouteChildType) => {
      const Guard = element.guard || ParentGuard || React.Fragment

      return (
        <Route
          key={`route-${id}-${element.name}`}
          exact
          path={`/${(rootPath + element.childPath).replace(/^\/+/, '')}`}
          render={(props: RouteComponentProps) => (
            <Guard>
              {element.component && <element.component {...props} />}
              {element.redirect && <Redirect to={element.redirect} />}
              <FlashMessagesProvider />
            </Guard>
          )}
        />
      )
    })
    : []

  return [parentRoute, ...childrenRoutes]
})

export const Routes: React.FC<RoutePropsType> = (props): JSX.Element => {
  const {
    routes, product, children,
  } = props
  const authContext = useContext(AuthContext)
  const [loaderTimeout, setLoaderTimeout] = useState(true)

  useEffect(() => {
    setTimeout(() => setLoaderTimeout(false), 5000)
  }, [])

  useEffect(() => {
    if (authContext.userData.userLoaded && loaderTimeout) {
      setTimeout(() => setLoaderTimeout(false), 400)
    }
  }, [authContext.userData.userLoaded])

  return (
    <>
      {loaderTimeout && <LoadingPage product={product} />}
      <BrowserRouter>
        {children}
        <Suspense fallback={<LoadingPage product={product} />}>
          <Switch>
            {renderRoutes(routes)}
            <Route
              render={() => (
                <Page404 />
              )}
            />
          </Switch>
        </Suspense>
      </BrowserRouter>
      {process.env.NX_ENV !== 'PROD' && (
        <AuthContext.Consumer>
          {(contextValues) => (
            <div
              style={{
                position: 'fixed',
                right: '30px',
                bottom: '100px',
                backgroundColor: 'rgba(255,239,196)',
                opacity: 0.55,
                padding: '10px',
                zIndex: 99999,
              }}
            >
              <span>
                {'logged as: '}
                {contextValues.userData?.user?.email}
              </span>
              <br />
              <span>
                {'is email verified: '}
                {contextValues.userData?.user?.emailVerified ? 'YES' : 'NO'}
              </span>
              <br />
              <span>
                {'is user data loaded: '}
                {contextValues.userData.userLoaded ? 'YES' : 'NO'}
              </span>
              <br />
              {contextValues?.userData?.user?.email
            && (
              <strong>
                <Link
                  onClick={() => {
                    redirectToSignOut()
                  }}
                >
                  Sign out
                </Link>
              </strong>
            )}
            </div>
          )}
        </AuthContext.Consumer>
      )}
    </>
  )
}
